import { useKeycloakJWTInfo } from '@app/auth/keycloak';
import { useGetCompanyQuery } from '@connect-carrier/generated/queries/getCompanyInfo';
import { useCompanyInfo } from '@connect-carrier/hooks/useCompanyInfo';
import { useFetchCustomerList } from '@connect-carrier/hooks/useFetchCustomerList';
import { FullstoryApprovedUserShape, setUserVars } from '@utils/fullstory';
import { setTzdbFullstoryVars } from '@utils/tzdbObservability';
import { FC, PropsWithChildren, useEffect, useMemo, useRef } from 'react';

export const PermissionsProvider: FC<PropsWithChildren<unknown>> = ({
  children,
}) => {
  const userData = useKeycloakJWTInfo();
  const companyInfo = useCompanyInfo();
  const hasSetVarsRef = useRef(false);
  const userName = `${userData?.given_name} ${userData?.family_name}` || '';
  const { customerList, loading: customerListLoading } = useFetchCustomerList();
  const companyName = companyInfo?.name;
  const companyId = companyInfo?.id;

  const { data: companyData } = useGetCompanyQuery({
    variables: { companyInput: { id: companyId ?? '' } },
    skip: !companyId,
  });

  const fullStoryVars: FullstoryApprovedUserShape = useMemo(() => {
    const carrierPortalRoles =
      (userData?.realm_access?.roles &&
        userData?.realm_access?.roles.filter((str) =>
          str.startsWith('connect-carrier'),
        )) ||
      [];
    const portalActiveTenants =
      (customerList &&
        customerList.map((customer) => customer?.displayName || '')) ||
      [];
    return {
      displayName: userName,
      portalUserName: userName,
      email: userData?.email,
      userId: userData?.MMUserID,
      company: companyName,
      portalRoles: carrierPortalRoles,
      portalActiveTenants: portalActiveTenants,
      portalIdentifierType: companyData?.getCompany?.identifierType,
      portalIdentifierNumber: companyData?.getCompany?.identifier,
    };
  }, [
    companyData?.getCompany?.identifier,
    companyData?.getCompany?.identifierType,
    companyName,
    customerList,
    userData?.MMUserID,
    userData?.email,
    userData?.realm_access?.roles,
    userName,
  ]);

  useEffect(() => {
    if (companyName && !customerListLoading && !hasSetVarsRef.current) {
      hasSetVarsRef.current = true;
      setUserVars(fullStoryVars);
      setTzdbFullstoryVars();
    }
  }, [companyName, customerListLoading, fullStoryVars]);

  if (companyName) {
    return <>{children}</>;
  }

  if (companyInfo?.error) {
    return <div css={{ padding: 50, fontSize: 16 }}>{companyInfo?.error}</div>;
  }

  return null;
};
